import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL;

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function ScorePill({ score, max, percentage }) {
  if (score == null) return <span className="text-[#484f58] font-mono text-xs">marking…</span>;
  const colour =
    percentage >= 75 ? 'text-[#3fb950]' :
    percentage >= 50 ? 'text-[#d29922]' :
    'text-[#f85149]';
  return (
    <span className={`font-mono text-xs font-bold ${colour}`}>
      {score}/{max} <span className="font-normal text-[#8b949e]">({percentage}%)</span>
    </span>
  );
}

function StatusBadge({ status }) {
  if (status === 'completed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#3fb950]/10 border border-[#3fb950]/30 text-[#3fb950] text-[10px] font-mono">
        <span className="w-1.5 h-1.5 rounded-full bg-[#3fb950]" />
        completed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#d29922]/10 border border-[#d29922]/30 text-[#d29922] text-[10px] font-mono">
      <span className="w-1.5 h-1.5 rounded-full bg-[#d29922] animate-pulse" />
      pending
    </span>
  );
}

export default function ExamHistoryList({ getToken, onSelectExam, onClose }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/api/exams`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setExams(data);
      } catch (err) {
        if (!cancelled) setError('Could not load exam history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [getToken]);

  return (
    <>
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-[#30363d] bg-[#0d1117]">
        <span className="text-xs font-mono text-[#8b949e]">My Exams</span>
        <button
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center rounded-full text-[#484f58] hover:text-[#e6edf3] hover:bg-[#30363d] transition-colors text-xs"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-5">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <span className="w-5 h-5 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="text-[#f85149] text-xs font-mono text-center mt-8">{error}</div>
        )}

        {!loading && !error && exams.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
            <div className="w-10 h-10 rounded-full bg-[#161b22] border border-[#30363d] flex items-center justify-center">
              <svg className="w-5 h-5 text-[#484f58]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-[#8b949e] text-xs font-mono">No exams yet</p>
            <p className="text-[#484f58] text-[10px]">Complete a Mock Exam to see your results here.</p>
          </div>
        )}

        {!loading && !error && exams.length > 0 && (
          <div className="rounded-xl border border-[#30363d] overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center px-4 py-2 bg-[#161b22] border-b border-[#30363d]">
              <span className="text-[10px] font-mono font-semibold text-[#484f58] uppercase tracking-wider">Date</span>
              <span className="text-[10px] font-mono font-semibold text-[#484f58] uppercase tracking-wider text-right">Score</span>
              <span className="text-[10px] font-mono font-semibold text-[#484f58] uppercase tracking-wider text-right">Status</span>
              <span className="text-[10px] font-mono font-semibold text-[#484f58] uppercase tracking-wider text-right"></span>
            </div>

            {/* Rows */}
            {exams.map((exam, i) => (
              <button
                key={exam.id}
                onClick={() => onSelectExam(exam.id)}
                className={`w-full grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center px-4 py-3 text-left hover:bg-[#161b22] transition-colors group ${
                  i < exams.length - 1 ? 'border-b border-[#30363d]' : ''
                }`}
              >
                <span className="text-xs text-[#e6edf3] font-mono">{formatDate(exam.submittedAt)}</span>
                <ScorePill score={exam.totalScore} max={exam.maxScore} percentage={exam.percentage} />
                <StatusBadge status={exam.status} />
                <svg
                  className="w-3.5 h-3.5 text-[#484f58] group-hover:text-[#58a6ff] transition-colors"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
